import {
  GetCatalogProductDetailsQuery,
  GetCatalogProductDetailsQueryVariables,
  useGetCatalogProductDetailsLazyQuery as useBaseLazyQuery,
  useGetCatalogProductDetailsQuery as useBaseQuery,
} from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';

// translates as:  most of the query variables, and other Query { options }
type UseGetCatalogProductDetailsQueryArgs = {
  catalogItemId: string;
  withPdpSections?: boolean;
  withProductRating?: boolean;
  withInventory?: boolean;
  queryOptions?: Omit<Parameters<typeof useBaseQuery>[0], 'variables'>;
};

const _mergeQueryResults = (data?: GetCatalogProductDetailsQuery) => {
  if (!data?.productCatalogItemById) {
    return;
  }
  return {
    ...data.productCatalogItemById,
    ...data.registryCatalogInventoryById,
  };
};

const _mergeVariablesForQuery = (args: UseGetCatalogProductDetailsQueryArgs): GetCatalogProductDetailsQueryVariables => {
  const { catalogItemId, withPdpSections, withProductRating, withInventory } = args;
  // we'll inject this ourselves;
  //   it should always be the current Event UUID,
  //   which is something that our callers shouldn't have to think about
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId ?? undefined;

  return {
    catalogItemId,
    eventId,
    withPdpSections: withPdpSections ?? true,
    withProductRating: withProductRating ?? false,
    withInventory: withInventory ?? false,
  };
};

export type MergedCatalogProductDetails = ReturnType<typeof _mergeQueryResults>;

export const useGetCatalogProductDetailsQuery = (args: UseGetCatalogProductDetailsQueryArgs) => {
  const { catalogItemId } = args;
  const { data, error, loading } = useBaseQuery({
    batchMode: 'fast',
    errorPolicy: 'ignore',
    // To avoid apollo cache entity conflicts, don't store the result of this query.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'no-cache',
    skip: !catalogItemId,
    variables: _mergeVariablesForQuery(args),
    ...args.queryOptions,
  });

  return {
    error,
    loading,
    product: _mergeQueryResults(data),
  };
};

export const useGetCatalogProductDetailsLazyQuery = (args: UseGetCatalogProductDetailsQueryArgs) => {
  const { catalogItemId } = args;
  const [sendRequest, { data, error, loading }] = useBaseLazyQuery({
    batchMode: 'fast',
    // To avoid apollo cache entity conflicts, don't store the result of this query.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'no-cache',
    // @ts-expect-error ts(2353)
    skip: !catalogItemId,
    variables: _mergeVariablesForQuery(args),
    ...args.queryOptions,
  });

  return {
    sendRequest,
    error,
    loading,
    product: _mergeQueryResults(data),
  };
};
